import React, { useState, useEffect } from "react";
import { Undo, RotateCcw } from "lucide-react";
import classNames from "classnames";
import { last, mapValues, shuffle } from "lodash";

import { Term } from "../../utils/interfaces";

import Step1 from "./Step1";
import Step2 from "./Step2";
import Step3 from "./Step3";
import Step4 from "./Step4";

import styles from "./StepSorter.module.css";
import { Stack } from "react-bootstrap";

type StepData = {
  1: Term[];
  2: Term[];
  3: Term[];
};

type StepSorterProps = {
  initialStep?: number;
  items: Array<Term>;
  label: string;
  nextLabel: string;
  setItems: (items: string) => void;
  setStepData: (items: StepData) => void;
  sortCategories: Array<{ label: string; name: string; rank: number }>;
  stepData: StepData;
};

type PileData = {
  items: Term[];
  label: string;
  name: string;
  rank: number;
};

const StepSorter: React.FC<StepSorterProps> = ({
  initialStep = 1,
  items,
  label,
  nextLabel,
  setItems,
  setStepData,
  sortCategories,
  stepData,
}) => {
  const [steps, setSteps] = useState([initialStep]);
  const step = steps[steps.length - 1];

  const topCategory = sortCategories.find(
    (category) => category.rank === 1
  )?.label;
  const initialPiles = sortCategories.map((category) => ({
    ...category,
    items: [],
  }));
  const [initialItems, setInitialItems] = useState<Term[]>([]);
  const [pileData, setPileData] = useState<PileData[]>(initialPiles);
  const [unsorted, setUnsorted] = useState<Term[]>([]);
  const [moved, setMoved] = useState<Term[]>([]);
  const [topList, setTopList] = useState<Term[]>([]);
  const [error, setError] = useState<string | null>(null);
  const topSelected = topList.filter((term) => term.selected);

  useEffect(() => {
    if (initialStep > 1) {
      setTopList(items);
      const newStepData = mapValues(stepData, (prevStep) =>
        prevStep.length ? prevStep : items
      );
      setStepData(newStepData);
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [steps]);

  useEffect(() => {
    const randomizedItems = shuffle(items);
    setInitialItems(randomizedItems);
    setUnsorted(randomizedItems);
  }, [items]);

  const handleSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();
    const formattedItems = topSelected.map((item) => item.term).join("\n");
    setItems(formattedItems);
  };

  const resetPile = () => {
    setPileData(initialPiles);
    setUnsorted(initialItems);
  };

  const undoMoveCard = () => {
    const lastItem = last(moved);
    if (lastItem) {
      setUnsorted((prev) => [lastItem, ...prev]);
      setMoved((prev) => prev.filter((i) => i.id !== lastItem.id));
      setPileData((previous) => {
        return previous.map((cat) => ({
          ...cat,
          items: cat.items.filter((i) => i.id !== lastItem.id),
        }));
      });
    }
  };

  const moveCard = (item: Term, category: string) => {
    setUnsorted((prev) => prev.filter((i) => i.id !== item.id));
    setMoved((prev) => [...prev, item]);
    setPileData((previous) => {
      return previous.map((cat) => {
        if (cat.name !== category) return cat;
        return {
          ...cat,
          items: [...cat.items, item],
        };
      });
    });
  };

  const itemsForRank = (rank: number) => pileData.find((pile) => pile.rank === rank)?.items || []
  const getTopItems = () => {
    const rank1Pile = itemsForRank(1)
    if (rank1Pile.length > 2) return rank1Pile;

    const rank2Pile = itemsForRank(2)
    let combinedPiles = [...rank1Pile, ...rank2Pile];
    const ranks = pileData.map((d) => d.rank);
    const maxRank = Math.max(...ranks);
    let nextRank = 3;
    // keep adding piles until there is at least 1 item
    // unless all the selections are from the lowest pile 
    while (combinedPiles.length === 0 && nextRank < maxRank) {
      const nextPile = itemsForRank(nextRank)
      nextRank += 1;
      combinedPiles = combinedPiles.concat(nextPile);
    }
    return combinedPiles;
  };

  const handleToggleTopList = (toggleItem: Term) => {
    setTopList((prev) => {
      return prev.map((item) => {
        if (item.id !== toggleItem.id) return item;
        return { ...item, selected: !item.selected };
      });
    });
  };

  const isSelected = (term: Term, data: Term[]): boolean => {
    return !!data.find((item) => item.term === term.term);
  };

  const handleBack = () => {
    const newSteps = steps.slice(0, steps.length - 1);
    const newStep = newSteps[newSteps.length - 1];

    setSteps(newSteps);
    if (newStep === 1) {
      resetPile();
    } else if (newStep === 2) {
      setTopList(
        stepData[1].map((t) => ({
          ...t,
          selected: isSelected(t, stepData[2]),
        }))
      );
    } else if (newStep === 3) {
      setTopList(
        stepData[2].map((t) => ({ ...t, selected: isSelected(t, stepData[3]) }))
      );
    }
  };

  const advanceFromStep1 = () => {
    // don't advance from pile sorter
    if (unsorted.length > 0) return null;
    const topItems = getTopItems();

    if (topItems.length === 0) {
      setError("Please reset and select at least 2 different categories");
      return
    }
    setError(null)
    const newStepData = { ...stepData, 1: topItems };
    // if less than 5 items selected, move directly to step 4
    if (topItems.length <= 5) {
      setTopList(topItems.map((t) => ({ ...t, selected: true })));
      setSteps((prev) => [...prev, 4]);
    } else if (topItems.length <= 10) {
      // move to step 3
      setTopList(topItems);
      newStepData[2] = topItems;
      setSteps((prev) => [...prev, 3]);
    } else {
      // move to step 2
      setTopList(topItems);
      setSteps((prev) => [...prev, 2]);
    }
    setStepData(newStepData);
  };

  const advanceStep = () => {
    if (step === 1) return advanceFromStep1();

    let nextStep = step + 1;
    if ([2, 3].includes(step) && topSelected.length === 0) {
      setError("Please select at least 1 item");
    } else if (step === 2 && topSelected.length > 10) {
      setError("Please select 10 or fewer items");
    } else if (step === 3 && topSelected.length > 5) {
      setError("Please select 5 or fewer items");
    } else {
      if (step === 2) {
        const newStepData = { ...stepData, 2: topSelected };
        if (topSelected.length <= 5) {
          nextStep = 4;
          newStepData[3] = topSelected;
        } else {
          setTopList(topSelected.map((t) => ({ ...t, selected: false })));
        }
        setStepData(newStepData);
      } else if (step === 3) {
        const newStepData = { ...stepData, 3: topSelected };
        setStepData(newStepData);
      }
      setError(null);
      setSteps((prev) => [...prev, nextStep]);
    }
  };

  const renderContent = () => {
    switch (step) {
      case 1:
        return (
          <Step1
            label={label}
            pileData={pileData}
            moveCard={moveCard}
            unsorted={unsorted}
            resetPile={resetPile}
            undoMoveCard={undoMoveCard}
          />
        );
      case 2:
        return (
          <Step2
            label={label}
            topList={topList}
            topCategory={topCategory}
            onToggle={handleToggleTopList}
          />
        );
      case 3:
        return (
          <Step3
            label={label}
            topList={topList}
            topCategory={topCategory}
            onToggle={handleToggleTopList}
          />
        );
      case 4:
        return <Step4 label={label} topFive={topSelected} />;
      default:
        break;
    }
  };

  const renderError = () => {
    if (!error) return null;
    return <p className={`text-end text-danger`}>{error}</p>;
  };

  const renderUndoReset = () => {
    if (step !== 1) return <div />;
    return (
      <div className={styles.undoRedoButtons}>
        <button className="button text" onClick={undoMoveCard}>
          <Undo />
          <span>Undo</span>
        </button>
        <button className="button text" onClick={resetPile}>
          <RotateCcw />
          <span>Reset</span>
        </button>
      </div>
    );
  };

  const renderButton = () => {
    const disabled = unsorted.length > 0 && step === 1;
    const classes = classNames("button", "primary", {
      disabled: disabled,
    });
    if (step === 4) {
      return (
        <div className={styles.buttonContainer4}>
          <button className={"button"} onClick={handleBack}>
            Back
          </button>
          <button
            className={classes}
            onClick={handleSubmit}
            disabled={disabled}
          >
            {nextLabel}
          </button>
        </div>
      );
    }

    if (step === 1) {
      return (
        <div className={styles.buttonContainer}>
          {steps.length > 1 ? (
            <button className={"button"} onClick={handleBack}>
              Back
            </button>
          ) : (
            <div />
          )}
          {renderUndoReset()}
          <button className={classes} onClick={advanceStep} disabled={disabled}>
            Next
          </button>
        </div>
      );
    }
    return (
      <div className={styles.buttonContainer4}>
        {steps.length > 1 ? (
          <button className={"button"} onClick={handleBack}>
            Back
          </button>
        ) : (
          <div />
        )}
        <button className={classes} onClick={advanceStep} disabled={disabled}>
          Next
        </button>
      </div>
    );
  };

  return (
    <Stack gap={3}>
      {renderContent()}
      {renderError()}
      {renderButton()}
    </Stack>
  );
};

export default StepSorter;
